import DT from 'duration-time-conversion';
import { getExt } from '../../utils';
import Sub from '../Sub';
import { parse } from '../webvttnotes/parser';
import srt2vtt from './srt2vtt';

export function url2sub(url) {
    return new Promise((resolve) => {
        const $video = document.createElement('video');
        const $track = document.createElement('track');
        $track.default = true;
        $track.kind = 'metadata';
        $video.appendChild($track);
        $track.onload = () => {
            resolve(
                Array.from($track.track.cues).map((item) => {
                    const start = DT.d2t(item.startTime);
                    const end = DT.d2t(item.endTime);
                    const text = item.text;
                    const Estart = DT.d2t(item.startTime);
                    const Eend = DT.d2t(item.endTime);
                    const Etext = item.text;
                    return new Sub({ start, end, text, Estart, Eend, Etext });
                }),
            );
        };
        $track.src = url;
    });
}

export function vtt2url(vtt) {
    return URL.createObjectURL(
        new Blob([vtt], {
            type: 'text/vtt',
        }),
    );
}

export function file2vtt(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async () => {
            const ext = getExt(file.name);
            if (ext === 'srt') {
                const text = reader.result.replace(/{[\s\S]*?}/g, '');
                const vtt = await srt2vtt(text);
                resolve(vtt);
            } else if (ext === 'vtt') {
                resolve(reader.result);
            } else {
                resolve([]);
                console.log('Unsupported File Format');
            }
        };
        reader.readAsText(file);
    });
}

export function file2sub(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async () => {
            const ext = getExt(file.name);
            if (ext !== 'srt') {
                resolve([]);
                console.log('Unsupported File Format');
            } else {
                const text = reader.result.replace(/{[\s\S]*?}/g, '');
                const vtt = srt2vtt(text);
                const url = vtt2url(vtt);
                const sub = await url2sub(url);
                resolve(sub);
            }
        };
        reader.readAsText(file);
    });
}

function getItemTime(Estart, start, exportEnglish, msLimiterTo, msLimiterFrom) {
    let time = '';
    if (exportEnglish) {
        time = Estart != null ? Estart : start;
    } else {
        time = start;
    }

    time = time.replace(msLimiterFrom, msLimiterTo);
    if (msLimiterTo === ',') {
        time = time.match(/\d\d:\d\d:\d\d,\d{3}/) ? time : time + ',000';
    } else {
        time = time.match(/\d\d:\d\d:\d\d.\d{3}/) ? time : time + '.000';
    }

    return time;
}

function getItemText(Etext, text, exportEnglish) {
    return exportEnglish ? (Etext === undefined ? text : Etext) : text;
}

function getNote(note, bookmark) {
    if (!note && !bookmark) {
        return '';
    }

    if (note || bookmark) {
        return `\nNOTE${bookmark ? ' BOOKMARK' : ''}${note ? ' ' + note : ''}`;
    }
}

export function sub2vtt(sub, exportEnglish) {
    return (
        'WEBVTT\n\n' +
        sub
            .map((item, index) => {
                return (
                    getItemTime(item.Estart, item.start, exportEnglish, '.', ',') +
                    ' --> ' +
                    getItemTime(item.Eend, item.end, exportEnglish, '.', ',') +
                    '\n' +
                    getItemText(item.EText, item.text, exportEnglish) +
                    getNote(item.note, item.bookmark)
                );
            })
            .join('\n\n')
    );
}

export function sub2srt(sub, exportEnglish) {
    return sub
        .map((item, index) => {
            return (
                index +
                1 +
                '\n' +
                getItemTime(item.Estart, item.start, exportEnglish, ',', '.') +
                ' --> ' +
                getItemTime(item.Eend, item.end, exportEnglish, ',', '.') +
                '\n' +
                getItemText(item.EText, item.text, exportEnglish)
                // If export english is true, then check if Etext is present , if not then use text
                // Etext will not be present when the page is loaded the first time and no other language is selected.
            );
        })
        .join('\n\n');
}

export function sub2txt(sub, exportEnglish) {
    return sub.map((item) => getItemText(item.EText, item.text, exportEnglish)).join('\n\n');
}

function toTime(seconds) {
    var date = new Date(null);
    date.setSeconds(seconds);
    return new Date(seconds * 1000).toISOString().substr(11, 12);
}

export function vttTime2SubTime(time) {
    let finalTime;
    let convertedTime = toTime(time);
    if (convertedTime) {
        let splitParts = convertedTime.split(':');
        let firstPart = splitParts[0].split('.');
        let secondPart = splitParts[1].split('.');
        let thirdPart = splitParts[2].split('.');
        finalTime = firstPart[0] + ':' + secondPart[0] + ':' + thirdPart[0];
        if (thirdPart[1] !== undefined) {
            finalTime = finalTime + '.' + thirdPart[1];
        }
    }

    return finalTime;
}

export const getSubNodesfromVtt = (subtitleData) => {
    const nodes = parse(subtitleData, { strict: false });

    let timelineData = nodes.cues.map(({ start, end, text, note, bookmark }) => {
        let starttimes = vttTime2SubTime(start);

        let endtimes = vttTime2SubTime(end);

        // text will be in the format - option 1
        // text \n\n
        // NOTE notetext \n\n
        // note variable should be notetext
        // bookmark variable should be false

        // text will be in the format - option 2
        // text \n\n
        // NOTE BOOKMARK notetext \n\n
        // note variable should be notetext
        // bookmark variable should be true

        // text will be in the format - option 32
        // text \n\n
        // NOTE BOOKMARK \n\n
        // note variable should be empty string
        // bookmark variable should be true
        if (note) {
            note = note.replace('NOTE', '');
        }

        return { start: starttimes, end: endtimes, text: text, note: note, bookmark: bookmark };
    });

    let cleanedData = timelineData.filter((res) => res.start !== res.end);

    const stateUpdated = cleanedData.map((item, index) => {
        return {
            ...item,
            text2: cleanedData[index]?.text,
        };
    });

    return stateUpdated;
};

export function createCue(subtitleBlock) {
    const convertTimeToSeconds = (timeString) => {
        return timeString.split(':').reduce((acc, time) => 60 * acc + +time);
    };

    // Convert subtitle block note and bookmark as fields to text like below
    // text2 will be in the format - option 1
    // text \n\n
    // NOTE notetext \n\n
    // note variable should be notetext
    // bookmark variable should be false

    // text2 will be in the format - option 2
    // text \n\n
    // NOTE BOOKMARK notetext \n\n
    // note variable should be notetext
    // bookmark variable should be true

    // text2 will be in the format - option 32
    // text \n\n
    // NOTE BOOKMARK \n\n
    // note variable should be empty string
    // bookmark variable should be true

    return {
        identifier: '',
        start: convertTimeToSeconds(subtitleBlock.start),
        end: convertTimeToSeconds(subtitleBlock.end),
        text: subtitleBlock.text2,
        note: subtitleBlock.note,
        bookmark: subtitleBlock.bookmark,
        Estart: convertTimeToSeconds(subtitleBlock.start),
        Eend: convertTimeToSeconds(subtitleBlock.end),
        Etext: subtitleBlock.text2,
        styles: 'line:13 position:50% align:center size:80%',
    };
}

/**
 * Converts VTT format string to SRT format string
 * @param {string} vtt - The VTT format string to convert
 * @return {string} The converted SRT format string
 */
export function vtt2srt(vtt) {
    if (!vtt) return '';

    // Remove WEBVTT header
    let srt = vtt.replace(/^WEBVTT\s*\n/i, '');

    // Handle empty content
    if (!srt.trim()) return '';

    // Process each subtitle block
    const blocks = srt.trim().split(/\n\s*\n/);
    let srtContent = '';
    let index = 1;

    for (let block of blocks) {
        // Skip empty blocks
        if (!block.trim()) continue;

        // Split into lines
        const lines = block.split('\n').filter((line) => line.trim());
        if (lines.length < 2) continue;

        // Extract time line and handle format (could be first or second line depending on if there's an index)
        let timeLine = '';
        let textLines = [];

        if (lines[0].includes('-->')) {
            timeLine = lines[0];
            textLines = lines.slice(1);
        } else if (lines[1].includes('-->')) {
            // If first line is an index, ignore it and use the next one
            timeLine = lines[1];
            textLines = lines.slice(2);
        } else {
            // Invalid block format
            continue;
        }

        // Remove positioning information (line:13 position:50% align:center size:80% etc.)
        timeLine = timeLine.replace(/-->(.+?)(?:line|position|align|size).*$/, '-->$1').trim();

        // Convert timestamp format (00:00:00.000 --> 00:00:00.000 to 00:00:00,000 --> 00:00:00,000)
        timeLine = timeLine.replace(/(\d{2}:\d{2}:\d{2})\.(\d{3})/g, '$1,$2');

        // Filter out NOTE and BOOKMARK lines
        const filteredTextLines = textLines.filter((line) => !line.trim().startsWith('NOTE'));

        // Add to SRT content
        srtContent += `${index}\n${timeLine}\n${filteredTextLines.join('\n')}\n\n`;
        index++;
    }

    return srtContent.trim();
}

/**
 * Fetches VTT content from a remote URL and converts it to SRT format
 * @param {string} url - The URL of the VTT file to convert
 * @return {Promise<string>} Promise resolving to the converted SRT content
 */
export async function remoteVtt2srt(url) {
    if (!url) {
        return Promise.reject(new Error('No URL provided'));
    }

    try {
        // Fetch the VTT content from the remote URL
        const response = await fetch(url, { cache: 'no-cache' });

        if (!response.ok) {
            throw new Error(`Failed to fetch VTT file: ${response.statusText}`);
        }

        const vttContent = await response.text();

        // Convert the VTT content to SRT format using the existing utility
        return vtt2srt(vttContent);
    } catch (error) {
        console.error('Error converting remote VTT to SRT:', error);
        return Promise.reject(error);
    }
}
